import React, { useCallback, useEffect, useState } from 'react';
import { MessageCircle, Send, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/apiError';

const MAX = 500;
const fmt = (d) => { try { return new Date(d).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } };

// Brief memories / comments under a gallery photo. Members post; authors and admins can remove.
export default function PhotoComments({ photoId, onCountChange, dark = true }) {
  const { user, listPhotoComments, addPhotoComment, deletePhotoComment } = useAuth();
  const [items, setItems] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const fg = dark ? 'var(--cream)' : 'var(--ink)';
  const sub = dark ? 'rgba(255,255,255,0.7)' : 'var(--olive)';

  const load = useCallback(() => { listPhotoComments(photoId).then(setItems).catch(() => setItems([])); }, [listPhotoComments, photoId]);
  useEffect(() => { setItems(null); setText(''); load(); }, [load]);
  useEffect(() => { if (items && onCountChange) onCountChange(photoId, items.length); }, [items, photoId, onCountChange]);

  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const c = await addPhotoComment(photoId, text); setItems((it) => [...(it || []), c]); setText(''); }
    catch (e) { toast.error(formatApiError(e, 'Could not post your comment.')); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm('Remove this comment?')) return;
    try { await deletePhotoComment(photoId, c.id); setItems((it) => it.filter((x) => x.id !== c.id)); }
    catch (e) { toast.error(formatApiError(e, 'Could not remove the comment.')); }
  };

  return (
    <div className="text-left" data-testid={`photo-comments-${photoId}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1.5 text-xs font-title mb-2" style={{ color: sub }}>
        <MessageCircle size={13} /> Memories &amp; comments {items ? <span data-testid="photo-comments-count">({items.length})</span> : null}
      </div>
      {items === null ? <p className="text-xs" style={{ color: sub }}>Loading…</p> : items.length === 0 ? (
        <p className="text-xs italic" style={{ color: sub }} data-testid="photo-comments-empty">No memories shared yet — be the first.</p>
      ) : (
        <ul className="space-y-2 max-h-40 overflow-y-auto pr-1" data-testid="photo-comments-list">
          {items.map((c) => (
            <li key={c.id} className="text-sm rounded px-3 py-2 relative" style={{ background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)', color: fg }} data-testid={`photo-comment-${c.id}`}>
              <div className="flex items-center justify-between gap-2 text-[11px]" style={{ color: sub }}>
                <span><b style={{ color: fg }}>{c.name}</b> · {fmt(c.created_at)}</span>
                {c.can_delete && <button onClick={() => remove(c)} title="Remove" className="p-0.5" data-testid={`photo-comment-delete-${c.id}`}><Trash2 size={12} /></button>}
              </div>
              <p className="mt-0.5 whitespace-pre-wrap break-words">{c.text}</p>
            </li>
          ))}
        </ul>
      )}
      {user ? (
        <div className="flex items-end gap-2 mt-2">
          <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, MAX))} rows={2} placeholder="Share a brief memory of this photo…"
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) post(); }}
            className="flex-1 px-3 py-2 rounded text-sm resize-none" style={{ background: dark ? 'rgba(255,255,255,0.12)' : 'white', color: fg, border: `1px solid ${dark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'}` }} data-testid="photo-comment-input" />
          <button onClick={post} disabled={busy || !text.trim()} className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title disabled:opacity-40" style={{ background: 'var(--gold)', color: '#000' }} data-testid="photo-comment-post">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Post
          </button>
        </div>
      ) : <p className="text-xs mt-2" style={{ color: sub }}>Sign in to share a memory.</p>}
      {user && <div className="text-[10px] mt-1 text-right" style={{ color: sub }}>{text.length}/{MAX}</div>}
    </div>
  );
}
